import { Fragment, memo, type ReactNode } from 'react';

import { PrivateWorkspaceAgentPanelMessagePillChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessagePillChip';
import { PrivateWorkspaceAgentPanelMessageSkillMoniker } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessageSkillMoniker';
import { WorkspaceAgentUnknownMonikerPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentUnknownMonikerPill';
import { type WorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { findWorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/utils/findWorkspaceAgentMessagePill';
import { splitPillMonikers } from '~/components/v2/components/workspace/workspace-agent-panel/utils/splitPillMonikers';

export type PrivateWorkspaceAgentInlineMonikerContentProps = {
  emptyPlaceholder?: ReactNode;
  pills: readonly WorkspaceAgentMessagePill[];
  value: string;
};

/** Plain-text with inline chips for stored monikers (read surfaces and hydration mirror). */
export const PrivateWorkspaceAgentInlineMonikerContent = memo(
  ({ emptyPlaceholder = '—', pills, value }: PrivateWorkspaceAgentInlineMonikerContentProps) => {
    if (!value) {
      return <>{emptyPlaceholder}</>;
    }

    const segments = splitPillMonikers(value);

    if (segments.length === 1 && segments[0]?.type === 'text' && segments[0].value === value) {
      return <>{value}</>;
    }

    let segmentOffset = 0;

    return (
      <>
        {segments.map((segment) => {
          if (segment.type === 'text') {
            const key = `text-${segmentOffset}`;
            segmentOffset += segment.value.length;

            return <Fragment key={key}>{segment.value}</Fragment>;
          }

          const key = segment.type === 'unknownMoniker' ? `unknown-${segmentOffset}` : `pill-${segmentOffset}`;
          segmentOffset += segment.raw.length;

          if (segment.type === 'unknownMoniker') {
            return (
              <WorkspaceAgentUnknownMonikerPill
                key={key}
                monikerId={segment.monikerId}
                monikerType={segment.monikerType}
              />
            );
          }

          if (segment.entityType === 'skill') {
            return <PrivateWorkspaceAgentPanelMessageSkillMoniker key={key} skillId={segment.monikerId} />;
          }

          return (
            <PrivateWorkspaceAgentPanelMessagePillChip
              isStreaming={false}
              key={key}
              pill={findWorkspaceAgentMessagePill(pills, segment.monikerId)}
              rawId={segment.monikerId}
            />
          );
        })}
      </>
    );
  },
);

PrivateWorkspaceAgentInlineMonikerContent.displayName = 'PrivateWorkspaceAgentInlineMonikerContent';
